import React, { useEffect, useState } from "react";
import clsx from "clsx";
import {
  buildLastSeenProductDeleted,
  type LastSeenProductDeleted
} from "@silversea-ssc/dotcom-api-types/UserEvents";
import type { LastSeenProduct } from "@silversea-ssc/dotcom-api-types/LastSeenProducts";
import { LastSeenProductsContent } from "components/LastSeenProducts/useLastSeenProductsContent";
import {
  Button,
  BackgroundColor,
  ProductTypeColor,
  ImageRatio,
  ResponsiveSanityImage
} from "@silversea-ssc/ui-components-react";
import { useSendUserEvent } from "src/utils/useSendUserEvent";
import { buildCruiseName } from "src/utils/cruise";
import * as styles from "./LastSeenProducts.module.scss";
import { legacyUrl } from "src/utils/routing/legacyUrl";
import CruiseDepartureArrivalDates from "components/cruises/CruiseDepartureArrivalDates";
import CruiseDuration from "components/cruises/CruiseDuration";

export type CardBackgroundColor = Extract<
  BackgroundColor,
  "white" | "neutral05"
>;

type Props = {
  lastSeenProduct: LastSeenProduct;
  content: LastSeenProductsContent;
  onDelete: (cruiseCode: string) => void;
  backgroundColor?: CardBackgroundColor;
};

const LastSeenProductCard: React.FC<Props> = ({
  lastSeenProduct,
  content,
  onDelete,
  backgroundColor = "neutral05"
}) => {
  const [deleteProduct, setDeleteProduct] = useState<
    LastSeenProductDeleted | undefined
  >(undefined);

  const { status, isLoading } = useSendUserEvent(deleteProduct);
  useEffect(() => {
    if (status === "success") onDelete(lastSeenProduct.cruiseCode);
  }, [status, onDelete, lastSeenProduct.cruiseCode]);

  const targetHref = legacyUrl(content.metadata.routing.path.localized);

  return (
    <ProductTypeColor
      productType={"ocean"} // TODO: fixme
    >
      <a data-content-piece="last_seen_products_card" href={targetHref}>
        <div className={styles.container}>
          <div
            className={clsx(styles.cardContainer, {
              [styles.cardContainerWhite]: backgroundColor === "white",
              [styles.cardContainerNeutral]: backgroundColor === "neutral05"
            })}
          >
            <div className={styles.imageContainer}>
              <ImageRatio ratio="auto" width={70} height={90}>
                <ResponsiveSanityImage
                  className={styles.image}
                  image={content.cardImage}
                  defaultWidth={70}
                  defaultHeight={90}
                  fit="crop"
                  loading="lazy"
                  alt={`Cruise ${content.cruiseCode}`}
                />
              </ImageRatio>
            </div>
            <div className={styles.cardInfo}>
              <div className={styles.cruiseDates}>
                <CruiseDepartureArrivalDates
                  departureDate={content.data.departureDate}
                  arrivalDate={content.data.arrivalDate}
                />
                <CruiseDuration days={content.data.days} withSeparator />
              </div>
              <h3 className={styles.cruiseTitle}>
                {departureToArrivalTitle(content)}
              </h3>
              <h4 className={styles.cruiseDestination}>
                {content.data.destination.name.localized}
              </h4>
            </div>
          </div>
          <Button
            iconName="close"
            variant="inline-text"
            size="small"
            className={styles.deleteButton}
            dataContentPiece="last_seen_products_card_delete_button"
            loading={isLoading && !!deleteProduct}
            aria-label="Delete"
            onClick={(e) => {
              // NOTE: this will prevent <a> to change page
              e.preventDefault();
              setDeleteProduct(
                buildLastSeenProductDeleted(lastSeenProduct.cruiseCode)
              );
            }}
          />
        </div>
      </a>
    </ProductTypeColor>
  );
};

const departureToArrivalTitle = (content: LastSeenProductsContent) =>
  buildCruiseName(
    content.data.departurePort?.name?.localized,
    content.data.arrivalPort?.name?.localized
  );

export default LastSeenProductCard;
